import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateField, FormErrorSummary, TextareaField, applyServerErrors, zRequiredString } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { addDaysToDateOnly, todayInDubai } from '@/shared/lib/dates';
import { formatDate } from '@/shared/lib/format';

import { useHoldDeal, useReleaseHold } from '../../api/mutations';

const holdSchema = z.object({ reason: zRequiredString(), until: z.string().optional() });
const releaseSchema = z.object({ note: z.string().optional() });

/** Puts the deal on hold (stage unchanged, internal SLA paused) or releases it. */
export function HoldDialog({ deal, release, onClose }: { deal: Schema<'Deal'>; release: boolean; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const hold = useHoldDeal(deal.id);
  const unhold = useReleaseHold(deal.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const today = todayInDubai();
  const holdForm = useForm<z.infer<typeof holdSchema>>({ resolver: zodResolver(holdSchema), defaultValues: { reason: '', until: addDaysToDateOnly(today, 14) } });
  const releaseForm = useForm<z.infer<typeof releaseSchema>>({ resolver: zodResolver(releaseSchema), defaultValues: { note: '' } });

  const fail = (e: unknown) => {
    if (!isApiError(e)) return;
    if (!release) applyServerErrors(holdForm, e, ['reason', 'until']);
    setProblem(e);
  };
  const submitHold = holdForm.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await hold.mutateAsync({ reason: v.reason.trim(), ...(v.until ? { until: v.until } : {}) });
      toast.done(v.until ? t('hold.done', { date: formatDate(v.until) }) : t('hold.doneOpen'));
      onClose();
    } catch (e) {
      fail(e);
    }
  });
  const submitRelease = releaseForm.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await unhold.mutateAsync(v.note?.trim() || undefined);
      toast.done(t('hold.released'));
      onClose();
    } catch (e) {
      fail(e);
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={release ? t('hold.releaseTitle', { number: deal.number }) : t('hold.title', { number: deal.number })}
        description={release ? t('hold.releaseBody') : t('hold.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={hold.isPending || unhold.isPending} onClick={() => void (release ? submitRelease() : submitHold())}>
              {release ? t('actions.release_hold.label') : t('actions.hold.label')}
            </Button>
          </>
        }
      >
        {problem ? (
          <div className="mb-3">
            <FormErrorSummary title={apiErrorMessage(problem)} />
          </div>
        ) : null}
        {release ? (
          <form onSubmit={(e) => void submitRelease(e)} className="flex flex-col gap-4">
            {deal.hold ? <p className="text-body text-ink-secondary" dir="auto">{deal.hold.reason}</p> : null}
            <TextareaField control={releaseForm.control} name="note" label={t('hold.releaseNote')} optional rows={2} />
            <button type="submit" hidden />
          </form>
        ) : (
          <form onSubmit={(e) => void submitHold(e)} noValidate className="flex flex-col gap-4">
            <TextareaField control={holdForm.control} name="reason" label={t('hold.reason')} rows={2} />
            <DateField control={holdForm.control} name="until" label={t('hold.until')} help={t('hold.untilHelp')} optional min={today} />
            <button type="submit" hidden />
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
